import React, { useEffect, useRef, useState } from 'react'
import { Table as AntTable, Tooltip } from 'antd'
import { STATUS, statusKeyFromRow, formatCount } from '../../theme/tokens'
import { toNum, isPctKey } from './utils/compareIecs'

// Same monetary columns the single-IEC summary table formats as currency.
const MONETARY_KEYS = [
  'total_amount', 'amount', 'amount_inr', 'amount_usd', 'value', 'total_fob_value', 'eo_fulfilled_val',
  'bal_eo_val', 'bal_eo_value', 'rodtep_scrip_amount', 'balance_amt', 'bcd_availed', 'bcd_missed_opp',
  'total_eligible', 'duty_saved', 'eo_prescribed'
]
const TEXT_KEYS = [
  'description', 'remark', 'final_remark', 'business_unit', 'lic_no', 'license_no', 'bond_no', 'status',
  'remarks', 'category', 'scheme', 'particulars', 'type', 'hsn_consistency_remark'
]
const ORDER = { years: 1, count: 2, amount: 3, percentage: 4, other: 5 }
const WIDTH = { years: 90, count: 90, amount: 124, percentage: 96, other: 100 }
const LABEL_WIDTH = 220

// Combined stays pinned on the right only while the pinned columns (description + Combined)
// leave the IEC groups at least this share of the table width; otherwise it scrolls too.
const PINNED_MAX_SHARE = 0.55

const classify = (dataIndex) => {
  const idx = String(dataIndex || '').toLowerCase()
  if (idx === 'summary' || idx === 'count') return 'count'
  if (MONETARY_KEYS.includes(idx)) return 'amount'
  if (isPctKey(idx)) return 'percentage'
  if (/^\d{4}$/.test(idx)) return 'years'
  return 'other'
}

const isBoldLabel = (label) => /total/i.test(label)

/**
 * Side-by-side summary for several IECs: the report's own description rows, every value
 * column repeated under each IEC, and a Combined group. The description column is pinned
 * left and Combined is pinned right (wide screens), so only the IEC groups scroll.
 */
const CompareIecTable = ({ cardName, iecs, headers, labels, combined, byLabel, currency, visibleColumns, loading, formatAmountCompact, formatAmountFull }) => {
  const wrapRef = useRef(null)
  const [tableWidth, setTableWidth] = useState(0)
  useEffect(() => {
    if (!wrapRef.current) return undefined
    const observer = new ResizeObserver(([entry]) => setTableWidth(entry.contentRect.width))
    observer.observe(wrapRef.current)
    return () => observer.disconnect()
  }, [])

  if (!headers || !headers.length) return null

  const isUsd = currency === 'USD'
  const currencyCode = isUsd ? 'USD' : 'INR'
  const isPendencyReport = cardName === 'Summary-Imp' || cardName === 'Summary-Exp'

  const labelHeader = headers.find(h => TEXT_KEYS.includes(h.dataIndex)) || headers[0]
  const metrics = headers
    .filter(h => h !== labelHeader && !TEXT_KEYS.includes(h.dataIndex))
    .map(h => ({ ...h, kind: classify(h.dataIndex) }))
    .filter(m => {
      if (m.kind === 'years') return visibleColumns.includes('count')
      if (m.kind === 'other') return true
      return visibleColumns.includes(m.kind)
    })
    .sort((a, b) => ORDER[a.kind] - ORDER[b.kind])

  // Combined shows only the pooled percentage(s); reports without one fall back to every column.
  const pctMetrics = metrics.filter(m => m.kind === 'percentage')
  const combinedMetrics = pctMetrics.length ? pctMetrics : metrics
  const combinedWidth = combinedMetrics.reduce((sum, m) => sum + WIDTH[m.kind], 0)
  const pinCombined = tableWidth > 0 && LABEL_WIDTH + combinedWidth <= tableWidth * PINNED_MAX_SHARE

  const metricTitle = (m) => {
    if (m.kind !== 'amount') return m.title
    const raw = m.title || 'Amount'
    const base = (raw === 'Value (INR)' || raw === 'Value (USD)') ? 'Amount' : raw.replace(/ \((₹|INR|\$|USD)\)/g, '')
    return `${base} (${isUsd ? '$' : '₹'})`
  }

  const metricValue = (row, m) => {
    if (!row) return null
    if (isUsd && m.kind === 'amount') {
      const usd = row[`${m.dataIndex}_usd`] ?? (m.dataIndex === 'amount_inr' ? row.amount_usd : undefined)
      return toNum(usd) ?? 0
    }
    if (isUsd && m.kind === 'years' && !isPendencyReport) return toNum(row[`${m.dataIndex}_amount_usd`]) ?? 0
    return row[m.dataIndex]
  }

  const renderMetric = (row, m) => {
    const value = metricValue(row, m)
    if (value === null || value === undefined || value === '') return <span className="cip-compare__empty">—</span>
    if (m.kind === 'amount') {
      return <span title={formatAmountFull(value, currencyCode)}>{formatAmountCompact(value, currencyCode)}</span>
    }
    if (m.kind === 'percentage') return `${toNum(value) ?? value}%`
    return formatCount(value)
  }

  // In warning / danger rows, shade the IEC with the highest share so the outlier stands out.
  const pctMetric = metrics.find(m => m.kind === 'percentage')
  const worstIecByLabel = {}
  if (pctMetric && iecs.length > 1) {
    labels.forEach((label, i) => {
      const key = statusKeyFromRow(combined[i])
      if (key !== 'warning' && key !== 'danger') return
      let worst = null
      iecs.forEach((iec, k) => {
        const v = toNum(byLabel[k].get(label)?.[pctMetric.dataIndex])
        if (v !== null && v > 0 && (!worst || v > worst.v)) worst = { iec: iec.iec, v }
      })
      if (worst) worstIecByLabel[label] = { iec: worst.iec, bg: STATUS[key].bg }
    })
  }

  const cellText = (record, content) => (
    <span style={{ color: record.color || 'inherit', fontWeight: record.bold ? 700 : 400 }}>{content}</span>
  )

  const groupColumns = (groupKey, rowFor, fixed, groupMetrics = metrics) => groupMetrics.map((m, i) => ({
    title: metricTitle(m),
    key: `${groupKey}|${m.dataIndex}`,
    align: 'right',
    width: WIDTH[m.kind],
    fixed,
    className: i === 0 ? 'cip-compare__group-start' : undefined,
    onCell: (record) => {
      const worst = worstIecByLabel[record.label]
      return worst && worst.iec === groupKey ? { style: { background: worst.bg } } : {}
    },
    render: (_, record) => cellText(record, renderMetric(rowFor(record), m)),
  }))

  const columns = [
    {
      title: labelHeader.title || 'Description',
      dataIndex: 'label',
      key: 'label',
      fixed: 'left',
      width: LABEL_WIDTH,
      render: (label, record) => cellText(record, label),
    },
    ...iecs.map((iec, k) => ({
      title: (
        <Tooltip title={iec.fullname}>
          <span className={`cip-compare__iec${iec.empty ? ' cip-compare__iec--empty' : ''}`}>
            {iec.short}<small>{iec.empty ? `${iec.iec} · No data` : iec.iec}</small>
          </span>
        </Tooltip>
      ),
      key: iec.iec,
      className: 'cip-compare__group-start',
      // A company with nothing to report shows dashes rather than a column of zeros.
      children: groupColumns(iec.iec, record => (iec.empty ? null : byLabel[k].get(record.label) || null)),
    })),
    {
      title: <span className="cip-compare__iec">Combined<small>{iecs.length} IECs</small></span>,
      key: '__combined',
      fixed: pinCombined ? 'right' : undefined,
      className: 'cip-compare__group-start cip-compare__combined',
      children: groupColumns('__combined', record => record.combined, pinCombined ? 'right' : undefined, combinedMetrics)
        .map(c => ({ ...c, className: `${c.className || ''} cip-compare__combined` })),
    },
  ]

  const dataSource = labels.map((label, i) => ({
    key: label,
    label,
    combined: combined[i],
    color: combined[i]?.color,
    bold: isBoldLabel(label),
  }))

  return (
    <div ref={wrapRef}>
      <AntTable
        className="cip-compare-table"
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        bordered
        loading={loading}
        scroll={{ x: 'max-content' }}
        rowClassName={(record) => (record.bold ? 'summary-bold-row' : '')}
      />
    </div>
  )
}

export default CompareIecTable
